/**
 * GTM tracking for <configurator-quiz>.
 *
 * One dataLayer push per shopper action, picked up in GTM through Custom Event
 * triggers — the container itself is loaded in layout/theme.liquid, this file
 * only feeds it. Parameters are pushed flat (`step_index`, not `quiz.step_index`)
 * so a Data Layer Variable is the bare parameter name, and so the quiz events
 * read the same way as the two GA4 ecommerce events below them.
 *
 * The quiz element owns the behaviour and calls in here; nothing in this file
 * changes what the quiz does, which is why the section can drop the script tag
 * and the quiz keeps working (see the `this.tracking?.` call sites).
 *
 * Events, and what fires them:
 *
 *   quiz_impression      half the quiz on screen, once per page view
 *   quiz_start           first answer or first Next, once per attempt
 *   quiz_question_view   a step becomes visible, back-navigation included
 *   quiz_answer          an answer settles — on click, or on leaving the step
 *                        for a multi-choice group
 *   quiz_back            the Back button
 *   quiz_skip            the Skip button on an optional step
 *   quiz_complete        the last step submitted, before the products load
 *   quiz_results_view    the grid rendered with at least one product
 *   quiz_no_results      the grid rendered empty — never both
 *   quiz_filter_modify   a facet changed on the result screen
 *   quiz_restart         the Restart button
 *   quiz_cta_click       any [data-ccq-cta] inside the quiz
 *   view_item_list       each page of cards appended to the grid
 *   select_item          a product clicked in the grid
 */

/** One sessionStorage record per quiz, so two quizzes cannot share an attempt. */
const SESSION_PREFIX = "ccq:session:";

/** GA4 drops parameter values longer than this. */
const MAX_PARAM_LENGTH = 100;

/** GA4 takes at most 200 items in one ecommerce push. */
const MAX_ITEMS = 200;

const ITEM_LIST_ID = "quiz_results";

const uid = (prefix) =>
  `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

/** Trailing segment of a GID — GA4 wants the numeric id, not the URI. */
const lastSegment = (gid) => String(gid || "").split("/").pop();

/**
 * Cut an over-long parameter at the last separator that still fits, so a
 * truncated list of answers ends on a whole answer rather than mid-handle.
 */
const clamp = (text, separator) => {
  const value = String(text == null ? "" : text);
  if (value.length <= MAX_PARAM_LENGTH) return value;
  const cut = value.slice(0, MAX_PARAM_LENGTH);
  const edge = separator ? cut.lastIndexOf(separator) : -1;
  return edge > 0 ? cut.slice(0, edge) : cut;
};

/** Seconds to one decimal, the shape GA4 reports a custom metric in. */
const elapsed = (since) => Math.round((Date.now() - since) / 100) / 10;

/**
 * The stable id of a question. Metaobject handles come from the section
 * (data-question-id); the positional group id is the fallback for a quiz whose
 * metaobjects were created without one.
 */
const questionIdOf = (element) =>
  (element &&
    (element.dataset.questionId ||
      element.dataset.groupId ||
      element.dataset.stepName)) ||
  "";

/** The stable id of an answer: the configurator_tag's handle. */
const answerIdOf = (option) =>
  (option && (option.dataset.handle || option.dataset.value)) || "";

/** Drop the keys GA4 has no value for, rather than sending nulls. */
const compact = (object) => {
  Object.keys(object).forEach((key) => {
    if (object[key] === undefined || object[key] === "") delete object[key];
  });
  return object;
};

class ConfiguratorQuizTracking {
  constructor(quiz) {
    this.quiz = quiz;
    this.quizId =
      quiz.dataset.quizId || quiz.dataset.collectionHandle || "configurator";
    this.placement = quiz.dataset.placement || "";
    this.listName = `Quiz Results - ${this.quizId}`;
    this.sessionKey = `${SESSION_PREFIX}${this.quizId}`;

    // In the theme editor the merchant is clicking through their own quiz to
    // build it — that is not shopper behaviour, and GTM is loaded there too.
    this.disabled = Boolean(quiz.isDesignMode);
    // Set while the quiz restores itself from a shared link: painting the step
    // that link names is not a question the shopper walked to.
    this.suspended = false;

    this.step = -1; // the step whose quiz_question_view is in force
    this.seenSteps = new Set(); // for is_revisit
    this.answered = new Set(); // groups answered at least once, for is_change
    this.pending = new Set(); // multi-choice groups awaiting their step submit
    this.stepEnteredAt = Date.now();
    this.pendingFilter = null; // a facet change waiting for its after-count
    this.resultCount = 0;
  }

  /* -------------------------------------------------------------- session */

  /**
   * One record per quiz attempt. It lives in sessionStorage because the quiz
   * restores itself from the URL after a reload — the same attempt has to keep
   * the same session_id, or the funnel breaks in two at every refresh.
   */
  get session() {
    if (this._session) return this._session;

    let stored = null;
    try {
      stored = JSON.parse(window.sessionStorage.getItem(this.sessionKey));
    } catch (error) {
      stored = null; // private mode, or a record from an older shape
    }

    this._session =
      stored && stored.id
        ? stored
        : { id: uid("qs"), startedAt: Date.now(), started: false, backs: 0 };
    return this._session;
  }

  saveSession() {
    try {
      window.sessionStorage.setItem(this.sessionKey, JSON.stringify(this.session));
    } catch (error) {
      // Storage is a convenience here; the in-memory record still carries the
      // attempt for as long as the page lives.
    }
  }

  /** Retire the attempt. The next event opens a new session_id. */
  clearSession() {
    this._session = null;
    try {
      window.sessionStorage.removeItem(this.sessionKey);
    } catch (error) {
      // Nothing to clean up if it was never written.
    }
  }

  /* ----------------------------------------------------------------- push */

  push(event, params) {
    if (this.disabled || this.suspended) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event,
      quiz_id: this.quizId,
      session_id: this.session.id,
      ...params,
    });
  }

  /**
   * GA4 merges the item array of an earlier ecommerce push into a later one,
   * so every push here is preceded by a null that clears it.
   */
  pushEcommerce(event, ecommerce) {
    if (this.disabled || this.suspended) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event,
      quiz_session_id: this.session.id,
      ecommerce,
    });
  }

  suspend() {
    this.suspended = true;
  }

  resume() {
    this.suspended = false;
  }

  /* -------------------------------------------------------------- answers */

  /**
   * The answers currently in force, as `{ questionId, ids[], labels[] }` — one
   * entry per active group holding a selection. A group the branching or the
   * conditional rules took out of play is left out: it never asked anything, so
   * it is not part of what the shopper answered.
   */
  answers() {
    const rows = [];
    this.quiz.querySelectorAll(".ccq__group").forEach((group) => {
      const groupId = group.dataset.groupId;
      if (!this.quiz.isGroupActive(groupId)) return;
      const selected = this.quiz.selections.get(groupId);
      if (!selected || selected.size === 0) return;

      const options = Array.from(selected)
        .map((value) => group.querySelector(`.ccq__opt[data-value="${value}"]`))
        .filter(Boolean);
      if (options.length === 0) return;

      rows.push({
        questionId: questionIdOf(group),
        ids: options.map(answerIdOf),
        labels: options.map((option) => option.dataset.label || ""),
      });
    });
    return rows;
  }

  /** Every group the shopper was actually asked, answered or not. */
  activeGroups() {
    return Array.from(this.quiz.querySelectorAll(".ccq__group")).filter(
      (group) => this.quiz.isGroupActive(group.dataset.groupId),
    );
  }

  /**
   * `question:answer` pairs, pipe-joined — a string rather than an array,
   * because GA4 flattens arrays unpredictably.
   */
  filtersApplied(rows) {
    return clamp(
      rows.map((row) => `${row.questionId}:${row.ids.join(",")}`).join("|"),
      "|",
    );
  }

  /** A short slug of the whole answer set, for grouping profiles in reports. */
  profile(rows) {
    return clamp(
      rows.flatMap((row) => row.ids).join("_"),
      "_",
    );
  }

  /** The 1-based position of a step on the path the shopper is walking. */
  position(index) {
    return this.quiz.path.indexOf(index) + 1;
  }

  /* --------------------------------------------------------------- events */

  /**
   * quiz_impression — half the quiz on screen, once per page view.
   *
   * Half of a quiz taller than the viewport can never be visible at once, so a
   * flat 0.5 threshold would silently never fire on mobile. Fall back to the
   * share of it that does fit.
   */
  observe() {
    if (this.disabled || typeof IntersectionObserver === "undefined") return;

    const height = this.quiz.getBoundingClientRect().height;
    const threshold =
      height > 0 ? Math.min(0.5, (window.innerHeight * 0.9) / height) : 0.5;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        this.push("quiz_impression", {
          placement: this.placement,
          device: window.innerWidth < 768 ? "mobile" : "desktop",
        });
      },
      { threshold },
    );
    observer.observe(this.quiz);
  }

  /**
   * quiz_start — the first real interaction, never the render: a start that
   * fires on paint makes the completion rate meaningless.
   */
  start(entryPoint) {
    if (this.disabled || this.session.started) return;
    this.session.started = true;
    this.session.startedAt = Date.now();
    this.saveSession();
    this.push("quiz_start", {
      entry_point: entryPoint || this.placement,
      total_steps: this.quiz.path.length,
    });
  }

  /**
   * quiz_question_view. render() runs on every answer, not only on navigation,
   * so the step is deduped here rather than at the call site.
   */
  questionView(index) {
    if (index === this.step) return;
    this.step = index;
    this.stepEnteredAt = Date.now();

    const isRevisit = this.seenSteps.has(index);
    this.seenSteps.add(index);

    this.push("quiz_question_view", {
      step_index: this.position(index),
      total_steps: this.quiz.path.length,
      question_id: questionIdOf(this.quiz.steps[index]),
      is_revisit: isRevisit,
    });
  }

  /**
   * A single-choice answer is settled the moment it is clicked. A multi-choice
   * group is not — the shopper is still building it — so it is buffered and
   * flushed as one event when they leave the step.
   */
  answer(group) {
    if (group.dataset.type === "multi_choice") {
      this.pending.add(group.dataset.groupId);
      return;
    }
    this.pushAnswer(group, "single_select");
  }

  /** Flush the buffered multi-choice answers of the step being left. */
  submitStep(index = this.step) {
    if (index < 0 || !this.quiz.steps[index]) return;
    this.quiz.groupsIn(index).forEach((group) => {
      if (!this.pending.delete(group.dataset.groupId)) return;
      this.pushAnswer(group, "multi_select");
    });
  }

  /** Drop a step's buffered answers without reporting them (skip, restart). */
  dropStep(index) {
    if (index < 0 || !this.quiz.steps[index]) return;
    this.quiz
      .groupsIn(index)
      .forEach((group) => this.pending.delete(group.dataset.groupId));
  }

  pushAnswer(group, type) {
    const groupId = group.dataset.groupId;
    const selected = Array.from(this.quiz.selections.get(groupId) || []);
    const options = selected
      .map((value) => group.querySelector(`.ccq__opt[data-value="${value}"]`))
      .filter(Boolean);
    // Clearing the last option of an optional group is not an answer, and the
    // spec has no event for un-answering — leave it to the next real one.
    if (options.length === 0) return;

    const isChange = this.answered.has(groupId);
    this.answered.add(groupId);

    this.push("quiz_answer", {
      step_index: this.position(this.quiz.steps.indexOf(group.closest(".ccq__step"))),
      question_id: questionIdOf(group),
      answer_id: clamp(options.map(answerIdOf).join(","), ","),
      answer_label: clamp(
        options.map((option) => option.dataset.label || "").join(", "),
        ",",
      ),
      answer_type: type,
      time_on_step: elapsed(this.stepEnteredAt),
      is_change: isChange,
    });
  }

  back(fromIndex, toIndex) {
    this.session.backs = (this.session.backs || 0) + 1;
    this.saveSession();
    this.push("quiz_back", {
      from_step: this.position(fromIndex),
      to_step: this.position(toIndex),
      from_question_id: questionIdOf(this.quiz.steps[fromIndex]),
    });
  }

  skip(index) {
    // A skipped step reports no answers, so anything the shopper had started
    // building in it is dropped rather than flushed.
    this.dropStep(index);
    this.push("quiz_skip", {
      step_index: this.position(index),
      question_id: questionIdOf(this.quiz.steps[index]),
      time_on_step: elapsed(this.stepEnteredAt),
    });
  }

  /** quiz_complete — the answer set as submitted, before the products load. */
  complete() {
    const rows = this.answers();
    this.push("quiz_complete", {
      answers_count: rows.length,
      skipped_count: Math.max(0, this.activeGroups().length - rows.length),
      duration_sec: Math.round((Date.now() - this.session.startedAt) / 1000),
      back_navigations: this.session.backs || 0,
      quiz_profile: this.profile(rows),
    });
  }

  /**
   * A facet change on the result screen. The event needs the count the change
   * produced, which only exists once the grid has been re-matched, so it is
   * queued here and pushed from results().
   */
  filterModify(group, input, action) {
    const option = group.querySelector(
      `.ccq__opt[data-value="${input.value}"]`,
    );
    this.pendingFilter = {
      filter_key: questionIdOf(group),
      filter_value: option ? answerIdOf(option) : input.value,
      action,
      result_count_before: this.resultCount,
    };
  }

  /**
   * The grid has been matched: flush the facet change that caused it, then
   * report the result screen — quiz_results_view or quiz_no_results, never
   * both.
   */
  results(count) {
    const rows = this.answers();
    const filtersApplied = this.filtersApplied(rows);
    const profile = this.profile(rows);

    if (this.pendingFilter) {
      const pending = this.pendingFilter;
      this.pendingFilter = null;
      this.push("quiz_filter_modify", {
        ...pending,
        result_count_after: count,
        filters_applied: filtersApplied,
      });
    }

    this.resultCount = count;

    if (count === 0) {
      this.push("quiz_no_results", {
        quiz_profile: profile,
        filters_applied: filtersApplied,
        filters_count: rows.length,
      });
      return;
    }

    this.push("quiz_results_view", {
      result_id: uid("qr"),
      result_count: count,
      quiz_profile: profile,
      filters_applied: filtersApplied,
      filters_count: rows.length,
      duration_sec: Math.round((Date.now() - this.session.startedAt) / 1000),
    });
  }

  /** A retake is a new attempt, so the session id is retired after the push. */
  restart(fromStep) {
    this.push("quiz_restart", {
      from_step: fromStep,
      previous_result_count: this.resultCount,
    });
    this.clearSession();
    this.seenSteps.clear();
    this.answered.clear();
    this.pending.clear();
    this.step = -1;
    this.resultCount = 0;
  }

  /** Any [data-ccq-cta] in the quiz — load more, reset filters, and anything a merchant adds. */
  cta(element) {
    this.push("quiz_cta_click", {
      cta_id: element.dataset.ccqCta,
      cta_label: clamp((element.textContent || "").trim(), " "),
      result_count: this.resultCount,
    });
  }

  /* ------------------------------------------------------------ ecommerce */

  /** One GA4 item from the catalogue entry the matcher already holds. */
  item(product, index) {
    return compact({
      item_id: lastSegment(product.variantId || product.id),
      item_name: product.title || product.handle,
      item_brand: product.vendor,
      item_category: product.productType,
      item_variant: product.variantTitle,
      price: product.price === undefined ? undefined : Number(product.price),
      currency: product.currency,
      index,
      item_list_id: ITEM_LIST_ID,
      item_list_name: this.listName,
      quantity: 1,
    });
  }

  /** Fired per page of cards appended, so the indexes match what was seen. */
  viewItemList(products, startIndex) {
    const items = products
      .slice(0, MAX_ITEMS)
      .map((product, offset) => this.item(product, startIndex + offset));
    if (items.length === 0) return;
    this.pushEcommerce("view_item_list", {
      item_list_id: ITEM_LIST_ID,
      item_list_name: this.listName,
      items,
    });
  }

  selectItem(product, index) {
    this.pushEcommerce("select_item", {
      item_list_id: ITEM_LIST_ID,
      item_list_name: this.listName,
      items: [this.item(product, index)],
    });
  }
}

window.ConfiguratorQuizTracking = ConfiguratorQuizTracking;
